import { useState } from "react";
import { X, Check, Sparkles, Globe2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * DomainPlanPickerModal — shown when the user clicks "Add domain" and needs to
 * purchase a new per-domain subscription slot. Each domain on the account can be
 * on its own tier (Starter $3, Growth $6, Pro $9 per month), letting users mix
 * and match (e.g., Growth-tier primary + Starter-tier side-project domain).
 */
const DEFAULT_TIERS = [
  { id: "starter", name: "Starter", price_monthly: 3, max_aliases: 1,    max_destinations: 1,    blurb: "1 alias · 1 recipient" },
  { id: "growth",  name: "Growth",  price_monthly: 6, max_aliases: 5,    max_destinations: 3,    blurb: "5 aliases · 3 recipients each" },
  { id: "pro",     name: "Pro",     price_monthly: 9, max_aliases: 9999, max_destinations: 9999, blurb: "Unlimited aliases · unlimited recipients" },
];

const prettyAliases = (n) => (n >= 9999 ? "Unlimited" : n);
const prettyDests = (n) => (n >= 9999 ? "Unlimited" : n);

export default function DomainPlanPickerModal({ open, onClose, tiers, isAdmin, onAdminAdded }) {
  const [selected, setSelected] = useState("growth");
  const [submitting, setSubmitting] = useState(false);
  if (!open) return null;

  const list = (tiers && tiers.length) ? tiers : DEFAULT_TIERS;

  const proceed = async () => {
    setSubmitting(true);
    try {
      if (isAdmin) {
        await api.post(`/billing/add-domain-slot?plan_tier=${selected}`);
        toast.success(`${selected.charAt(0).toUpperCase() + selected.slice(1)} domain slot added (admin bypass)`);
        onAdminAdded && onAdminAdded(selected);
        return;
      }
      const { data } = await api.post("/billing/checkout", {
        package_id: `extra_domain_${selected}`,
        origin_url: window.location.origin,
      });
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/50 backdrop-blur-sm p-4" data-testid="domain-plan-picker-modal">
      <div className="w-full max-w-xl rounded-2xl bg-white border border-[#E2E8F0] shadow-xl overflow-hidden">
        <div className="flex items-start justify-between p-6 border-b border-[#E2E8F0]">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-xl bg-[#FEE7E1] grid place-items-center flex-shrink-0">
              <Globe2 className="h-5 w-5 text-[#F26953]" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-[#0F172A]">Add another domain</h3>
              <p className="text-xs text-[#475569] mt-0.5">Pick a tier for this domain. Each domain is billed independently — mix and match.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100" data-testid="domain-plan-picker-close"><X className="h-4 w-4" /></button>
        </div>

        <div className="p-6 space-y-3">
          {list.map((t) => {
            const active = selected === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setSelected(t.id)}
                data-testid={`domain-plan-tier-${t.id}`}
                className={`w-full text-left rounded-xl border p-4 transition-all ${
                  active
                    ? "border-[#F26953] bg-[#FFF8F6] ring-1 ring-[#F26953]/20"
                    : "border-[#E2E8F0] bg-white hover:border-slate-300"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-base font-bold text-[#0F172A]">{t.name}</span>
                      {t.id === "growth" && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#F26953] bg-[#FEE7E1] px-2 py-0.5 rounded-full">
                          <Sparkles className="h-2.5 w-2.5" /> Popular
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#475569] mt-1">{t.blurb || `${prettyAliases(t.max_aliases)} aliases · ${prettyDests(t.max_destinations)} recipients/alias`}</div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-right">
                      <div className="text-xl font-bold text-[#0F172A]">${t.price_monthly}</div>
                      <div className="text-[10px] text-slate-400 uppercase tracking-wider">/mo</div>
                    </div>
                    <div className={`h-5 w-5 rounded-full border-2 grid place-items-center flex-shrink-0 ${
                      active ? "border-[#F26953] bg-[#F26953]" : "border-slate-300 bg-white"
                    }`}>
                      {active && <Check className="h-3 w-3 text-white" />}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className="px-6 pb-6 space-y-3">
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-[#475569]">
            Billed as a separate monthly subscription. Prorated from today. Cancel anytime from the billing portal — your primary domain plan is unaffected.
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              data-testid="domain-plan-picker-cancel"
              className="flex-1 bg-white border border-[#E2E8F0] text-[#0F172A] font-semibold py-3 rounded-lg hover:bg-slate-50"
            >
              Maybe later
            </button>
            <button
              onClick={proceed}
              disabled={submitting}
              data-testid="domain-plan-picker-confirm"
              className="flex-1 bg-[#F26953] text-white font-semibold py-3 rounded-lg hover:bg-[#D85340] disabled:opacity-60"
            >
              {submitting ? "Redirecting…" : `Continue with ${list.find(t => t.id === selected)?.name} · $${list.find(t => t.id === selected)?.price_monthly}/mo`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
